// @flow
import _ from 'lodash';
import type { TCoachPlayerStatsById, TPlayersById, TRound } from "../../modules/types";
import { getTeamScore } from '../complex/team';

export type TLineup = Object;
type TLeague = Object;

export const getLineupIdsPositions = (lineup: TLineup) => {
	const lineReducer = item => _.reduce(item, (result, value) => {
		return {
			...result,
			...value
		};
	}, {});

	return _.chain(lineup)
		.reduce((result, value, key) => {
			const ids = _.map(value, id => {
				return { [id]: parseInt(key, 10) };
			});
			return {
				...result,
				...lineReducer(ids)
			};
		}, {})
		.value();
};

// const getProjLeaderId = (
// 	lineup: TLineup,
// 	coach_players_by_id: TCoachPlayerStatsById,
// 	proj_score_key: string
// ) => {
// 	const { captain, vice_captain } = lineup;
// 	let proj_leader_id = captain || vice_captain;

// 	if (proj_leader_id) {
// 		const
// 			proj_leader = coach_players_by_id[proj_leader_id],
// 			proj_leader_score = _.result(proj_leader, proj_score_key, 0);

// 		if (!proj_leader_score) {
// 			proj_leader_id = vice_captain;
// 		}
// 	}

// 	return proj_leader_id;
// };

export const getPlayersMatchStatus = (
	player_id: number,
	players_by_id: Object,
	round: Object
): boolean => {
	const player = players_by_id[player_id];
	const squad_id = _.get(player, 'squad_id');
	const match = round.matches.find(match =>
		match.home_squad_id === squad_id || match.away_squad_id === squad_id);

	if(!match) {
		return false;
	}

	return match.status;
};

export const playersMatchIsScheduled = (
	player_id: number,
	players_by_id: Object,
	round: Object
): boolean => {
	const status = getPlayersMatchStatus(player_id, players_by_id, round);
	return status === "scheduled";
};

const isTeamProjectedScoreReadyToBeCalculated = (
	lineup,
	players_by_id,
	coach_players_by_id,
	league,
	round
) =>
	_.isEmpty(lineup) ||
	_.isEmpty(players_by_id) ||
	_.isEmpty(coach_players_by_id) ||
	_.isEmpty(league) ||
	_.isEmpty(round);


// eslint-disable-next-line complexity
export const getTeamProjectedScore = (
	lineup: TLineup,
	players_by_id: TPlayersById,
	coach_players_by_id: TCoachPlayerStatsById,
	league: TLeague,
	round: TRound
) => {
	if(isTeamProjectedScoreReadyToBeCalculated(
		lineup,
		players_by_id,
		coach_players_by_id,
		league,
		round
	)) {
		return 0;
	}

	const pure_lineup = _.chain(lineup)
		.omit(['captain', 'vice_captain', 'bench', 'emergency'])
		.value();

	const team_players_ids = _.chain(pure_lineup)
		.values()
		.flatten()
		.filter(_.identity)
		.value();

	const round_id = round.id;
	const proj_score_key = `proj_scores.${round_id}`;
	const round_score_key = `stats.scores.${round_id}`;


	// get base score. pure_lineup
	// - projected for players with matches statuses of playing or scheduled
	// - actaully score for player with status of comlete
	const pure_lineup_projected_score = team_players_ids.reduce((accumulator, player_id) => {
		const match_status = getPlayersMatchStatus(player_id, players_by_id, round);
		const player = _.get(players_by_id, player_id);
		const coach_player = _.get(coach_players_by_id, player_id);
		const score = match_status === 'complete'? _.get(player, round_score_key, 0):
			_.get(coach_player, proj_score_key, 0);
		return accumulator + score;
	}, 0);

	// add leader score
	let leader_score = 0;
	if(lineup.captain) {
		const captain_match_status = getPlayersMatchStatus(lineup.captain, players_by_id, round);
		if(captain_match_status === 'complete') {
			leader_score = _.get(players_by_id, `${lineup.captain}.${round_score_key}`, 'DNP');
		}
		else {
			leader_score = _.get(coach_players_by_id, `${lineup.captain}.${proj_score_key}`, 0);
		}
	}
	// if captain set and had a dnp
	if(lineup.vice_captain && leader_score === 'DNP') {
		const vice_captain_match_status =
			getPlayersMatchStatus(lineup.vice_captain, players_by_id, round);
		if(vice_captain_match_status === 'complete') {
			leader_score = _.get(players_by_id, `${lineup.vice_captain}.${round_score_key}`, 'DNP');
		}
		else {
			leader_score =
				_.get(coach_players_by_id, `${lineup.vice_captain}.${proj_score_key}`, 0);
		}
	}
	// fix leader_score if dnp
	if(leader_score === 'DNP') {
		leader_score = 0;
	}


	// emerge scores
	const emergency_scores = Object.keys(pure_lineup).reduce((accumulator, lineup_id) => {

		// check if lineup_id has emerge set
		if(_.get(lineup, `emergency.${lineup_id}`, 0) === 0) {
			return accumulator;
		}

		const player_ids = pure_lineup[lineup_id];
		const number_of_dnps_in_lineup = player_ids.filter(player_id => {
			const match_status = getPlayersMatchStatus(player_id, players_by_id, round);
			if(match_status !== 'complete') {
				return false;
			}
			return _.get(players_by_id, `${player_id}.${round_score_key}`, 'DNP') === 'DNP';
		});

		// pos had dnp - look for emege score
		let emergency_score = 0;
		if(number_of_dnps_in_lineup > 0) {
			const emergency_id = _.get(lineup, `emergency.${lineup_id}`);
			const emergency = _.get(players_by_id, emergency_id);
			const coach_emergency = _.get(coach_players_by_id, emergency_id);
			const emergency_match_status =
			getPlayersMatchStatus(emergency_id, players_by_id, round);

			emergency_score = emergency_match_status === 'complete'?
				_.get(emergency, round_score_key, 0):
				_.get(coach_emergency, proj_score_key, 0);
		}
		return accumulator + emergency_score;
	}, 0);

	return pure_lineup_projected_score + leader_score + emergency_scores;
};

type TTeamScoreFromLadder = {
	team: Object,
	is_active_match: boolean,
	round: Object,
	start_round?: number,
	score?: number | string,
	players_by_id?: TPlayersById
}

export const getTeamScoreFromLadder = (
	{ team, is_active_match, round, players_by_id }: TTeamScoreFromLadder
) => {
	const players = players_by_id || {};
	// is active match calculate player scores
	if (is_active_match) {
		return getTeamScore({
			lineup: team.lineup,
			players_by_id: players,
			round
		});
	}
	// if not = get score from scoreflow

	return _.get(team, ['scoreflow', round.id], '--');
};

type TTotalScoreFromLadder = {
	team: Object,
	is_active_match: boolean,
	round_id: number,
	start_round?: number,
	score?: number | string,
	players_by_id?: TPlayersById
}

export const getTotalScoreFromLadder = (
	{ team, start_round, round_id, is_active_match, score } : TTotalScoreFromLadder
) => {
	const { scoreflow } = team;

	if (!scoreflow || _.isEmpty(scoreflow)) {
		return '--';
	}

	const end_round = is_active_match ? round_id - 1 : round_id;
	const initial_score = is_active_match && _.isNumber(score) ? score : 0;

	return _.reduce(scoreflow, (sum, points, id: string) => {
		if (end_round >= parseInt(id, 10)) {
			return sum + points;
		}
		return sum;
	}, initial_score);
};

export const leagueStatsSelected =
	(team_property: Object, round_id: number) => {
		const paired_by_key_value_team_stats = _.toPairs(team_property)
			.sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10))
			.slice(0, round_id);
		const filtered_team_stats = paired_by_key_value_team_stats
			.filter(scores => parseInt(scores[0], 10) <= round_id);
		const added_team_stats = _.sum(filtered_team_stats.map(point => point[1]));
		return added_team_stats;
	};

export const gamesPlayed =
	(team: Object, round_id: number) => {
		const league_scoreflow_array = _.toPairs(team.league_scoreflow)
			.sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10))
			.slice(0, round_id);
		const filtered_ls_array = league_scoreflow_array
			.filter(scores => parseInt(scores[0], 10) <= round_id);
		return filtered_ls_array.length;
	};

export const createWinLossRecord =
	(team: Object, round_id: number, recordForm: number = 0) => {
		const scoreflow = _.toPairs(team.league_scoreflow)
			.sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10))
			.slice(0, round_id).slice(recordForm);
		const filtered_scoreflow = scoreflow
			.filter(scores => parseInt(scores[0], 10) <= round_id);
		const recordKeys = {
			'2': 'win',
			'1': 'draw',
			'0': 'loss',
		};

		const record = filtered_scoreflow.reduce((result, [_, value]) => {
			const key = recordKeys[value];

			if(key) result[key]++;

			return result;
			  }, { win: 0, draw: 0, loss: 0 });

		return record;

	};

export const createWinLossForm = (team: Object, round_id: number) => {
	const last_five_record = createWinLossRecord(team, round_id, -5);
	return `${last_five_record.win}-${last_five_record.draw}-${last_five_record.loss}`;
};
export const MAX_TRADES = 5;

export const countPlayersWithMatchStatus = (
	player_ids: number[],
	players_by_id: Object,
	round: Object,
	status: string
) => {
	if (_.isEmpty(round) || _.isEmpty(player_ids)) {
		return 0;
	}
	return _(player_ids)
		.map(id => players_by_id[id])
		.filter(_.identity)
		.filter(player => {
			const squad_id = player.squad_id;
			const match = _.find(
				round.matches,
				({ away_squad_id, home_squad_id }) =>
					squad_id === away_squad_id || squad_id === home_squad_id
			);

			return match && match.status === status;
		})
		.value().length;
};
export const getYetToPlay = (player_ids: number[],  players_by_id: Object, round: TRound) => {
	if (round && round.status === "complete") {
		return 0;
	}

	return countPlayersWithMatchStatus(player_ids, players_by_id, round, 'scheduled');
};


// WEBPACK FOOTER //
// ./src/helpers/team/index.js